// src/components/InternalSentimentView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { ROUTES } from '../constants/routes';
import { formatDate } from '../utils';
import { CommunicationCard } from './SentimentListView';
import { DepartmentSelector } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { useEntityNavigationContext } from './EntityNavigation';
import { useStaffContext } from '../contexts/StaffContext';

const INITIAL_COMMS_LIMIT = 50;
const COMMS_PAGE_SIZE = 50;

const InternalSentimentView = () => {
    const { apiService, departmentId: departmentIdFromContext, dateRange, setDateRange } = useOutletContext();
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();
    const params = useParams();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);
    
    const [communicationsList, setCommunicationsList] = useState([]);
    const [loadingComms, setLoadingComms] = useState(false);
    const [errorComms, setErrorComms] = useState(null);
    const [commsLimit, setCommsLimit] = useState(INITIAL_COMMS_LIMIT);
    const [sentimentTrend, setSentimentTrend] = useState([]);
    const [loadingTrend, setLoadingTrend] = useState(false);

    // Load departments
    const loadDepartments = useCallback(async () => {
        if (!apiService) return;
        setLoadingDepartments(true);
        try {
            const deptResult = await apiService.getDepartmentList();
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [apiService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const handleDepartmentChange = (newDept) => {
        navigate(`${ROUTES.OPERATIONS}/${newDept}/sentiment`);
    };

    // DRI state (for consistency with other views, even if not filtering yet)
    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = useState([]);

    const loadDris = useCallback(async () => {
        if (!apiService || !selectedDepartment) return;
        try {
            const driResult = await apiService.getDepartmentDRIs(selectedDepartment);
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        }
    }, [apiService, selectedDepartment]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const hasActiveFilters = selectedDris.length > 0;
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
    };

    const loadDepartmentComms = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingComms(true); setErrorComms(null);
        try {
            // Get department communications with DRI filter
            const comms = await apiService.getDepartmentCommunications(
                selectedDepartment, 
                dateRange.start, 
                dateRange.end,
                selectedDris.length > 0 ? selectedDris : null,
                commsLimit
            );
            setCommunicationsList(comms);
        } catch (e) { setErrorComms(e.message); setCommunicationsList([]); }
        finally { setLoadingComms(false); }
    }, [apiService, selectedDepartment, dateRange, selectedDris, commsLimit]);

    const loadDepartmentSentimentTrend = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingTrend(true);
        try {
            const trendData = await apiService.getDepartmentSentimentOverTime(selectedDepartment, dateRange.start, dateRange.end);
            setSentimentTrend(trendData?.map(item => ({
                ...item,
                weekBeginning: formatDate(item.weekBeginning) 
          })) ?? []);
        } catch (err) { console.error("Error loading dept sentiment trend:", err); setSentimentTrend([]); }
        finally { setLoadingTrend(false); }
    }, [apiService, selectedDepartment, dateRange]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDepartmentComms();
            loadDepartmentSentimentTrend();
        }
    }, [selectedDepartment, loadDepartmentComms, loadDepartmentSentimentTrend, selectedDris]);

    useEffect(() => {
        setCommsLimit(INITIAL_COMMS_LIMIT);
    }, [selectedDepartment, dateRange, selectedDris]);

    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} driDisplayFormatter={getDriDisplayName}
                filterGroups={[{ id: 'context', label: 'Context', filters: [{ key: 'department' }] }]}
                filters={{ department: selectedDepartment }}
                filterConfigs={{ department: { label: 'Department', render: () => departments.length > 0 && <DepartmentSelector departments={departments} selectedDepartment={selectedDepartment} onDepartmentChange={handleDepartmentChange} loading={loadingDepartments} /> } }}
                activeFilterChips={[]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />
            
            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
                 <h3 className="text-lg font-medium text-gray-900 mb-4">Department Sentiment Trend (Weekly)</h3>
                 {loadingTrend ? <p>Loading chart...</p> : sentimentTrend.length > 0 ? (
                     <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={sentimentTrend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="weekBeginning" tick={{fontSize: 10}} />
                            <YAxis domain={[0, 1]} tickFormatter={(value) => `${(value * 100).toFixed(0)}%`} tick={{fontSize: 10}} />
                            <RechartsTooltip wrapperClassName="text-xs" formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Avg Sentiment']} labelFormatter={(value) => `Week: ${value}`} />
                            <Line type="monotone" dataKey="avgSentiment" stroke="#3B82F6" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} name="Avg Sentiment" />
                        </LineChart>
                      </ResponsiveContainer>
                  ) : (
                      <p className="text-center text-sm text-gray-500 py-4">No sentiment data available for this department.</p>
                  )}
             </div>

            <div
                className="rounded-xl p-4 md:p-6"
                style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
            >
                <h3 className="text-lg font-medium mb-1" style={{ color: 'var(--pallium-text-primary)' }}>
                    Department Communications
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Communications involving this department, with linked entities and usable call scores.
                </p>
                {loadingComms && <div className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>Loading communications...</div>}
                {errorComms && <div className="text-sm" style={{ color: 'var(--pallium-error-red)' }}>Error loading communications: {errorComms}</div>}
                {!loadingComms && !errorComms && communicationsList.length === 0 && (
                    <div className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>No communications found.</div>
                )}
                {!loadingComms && !errorComms && communicationsList.length > 0 && (
                    <>
                        <div className="space-y-4">
                            {communicationsList.map((comm) => (
                                <CommunicationCard
                                    key={comm.id || comm.record_id || `${comm.date}-${comm.title}`}
                                    comm={comm}
                                    departmentId={selectedDepartment}
                                    navigate={navigate}
                                />
                            ))}
                        </div>
                        {communicationsList.length >= commsLimit && (
                            <div className="mt-4 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => setCommsLimit((limit) => limit + COMMS_PAGE_SIZE)}
                                    className="px-4 py-2 rounded-lg text-sm font-medium"
                                    style={{ backgroundColor: 'var(--pallium-purple-0-5)', color: 'var(--pallium-primary-purple)' }}
                                    disabled={loadingComms}
                                >
                                    Load more communications
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default InternalSentimentView;